import { i18n } from '../i18n.js';
import { Firebase } from '../firebase-loader.js';
import { BootVeil } from './boot-veil.js';

/**
 * VERIFY YOUR EMAIL BEFORE THE ACCOUNT EXISTS (owner decision, launch audit
 * round 3, 2026-09-26: fake accounts must cost something).
 *
 * The server creates a NEW Moonfall account only for a verified email
 * (`users:getOrCreateUser` refuses with `email_verification_required`); Google
 * sign-in arrives verified, an email/password sign-up verifies through the link
 * Firebase mailed at sign-up. This screen waits for that link, then hands
 * control back so the sign-in continues exactly where it stopped — the consent
 * the player already gave is still in hand, so they are not asked twice.
 *
 * It wears the consent gate's frame (consent-gate.css): the same moment of the
 * same flow, so it looks like one.
 */

const EmailVerificationGate = (function () {
  let overlay = null;

  function uiText(key, fallback, replacements = {}) {
    const translated = i18n?.t?.(key, replacements);
    if (translated && translated !== key) return translated;
    return String(fallback).replace(/\{\{(\w+)\}\}/g, (match, token) => (
      Object.prototype.hasOwnProperty.call(replacements, token) ? String(replacements[token]) : match
    ));
  }

  function el(tag, attrs = {}, children = []) {
    const node = document.createElement(tag);
    for (const [key, value] of Object.entries(attrs)) {
      if (key === 'className') node.className = value;
      else if (key === 'textContent') node.textContent = value;
      else node.setAttribute(key, value);
    }
    for (const child of children) node.appendChild(child);
    return node;
  }

  function remove() {
    if (overlay && overlay.parentNode) overlay.parentNode.removeChild(overlay);
    overlay = null;
  }

  /**
   * Resolves `'verified'` once Firebase reports the address verified (the ID
   * token is already refreshed), or `'switch_account'` when the player chose to
   * sign in with another account instead.
   *
   * @param {{ email?: string }} [options]
   * @returns {Promise<'verified'|'switch_account'>}
   */
  function waitForVerification({ email = '' } = {}) {
    remove();
    return new Promise((resolve) => {
      const status = el('p', {
        className: 'email-verification-status',
        role: 'status',
        'aria-live': 'polite',
      });
      const continueButton = el('button', {
        type: 'button',
        className: 'consent-gate-submit',
        id: 'email-verification-continue',
        textContent: uiText('emailVerify.continue', "I've verified — continue"),
      });
      const resendButton = el('button', {
        type: 'button',
        className: 'email-verification-secondary',
        id: 'email-verification-resend',
        textContent: uiText('emailVerify.resend', 'Send the email again'),
      });
      const switchButton = el('button', {
        type: 'button',
        className: 'email-verification-link',
        id: 'email-verification-switch',
        textContent: uiText('emailVerify.switchAccount', 'Use another account'),
      });

      const modal = el('div', {
        className: 'consent-gate-modal email-verification-modal',
        role: 'dialog',
        'aria-modal': 'true',
        'aria-labelledby': 'email-verification-title',
      }, [
        el('h2', {
          className: 'consent-gate-title',
          id: 'email-verification-title',
          textContent: uiText('emailVerify.title', 'Verify your email'),
        }),
        el('p', {
          className: 'consent-gate-subtitle',
          textContent: uiText(
            'emailVerify.subtitle',
            'We sent a link to {{email}}. Open it, then come back here to finish creating your account.',
            { email: email || '' },
          ),
        }),
        continueButton,
        resendButton,
        status,
        switchButton,
      ]);
      overlay = el('div', { className: 'consent-gate-overlay visible email-verification-overlay' }, [modal]);
      document.body.appendChild(overlay);
      BootVeil?.drop?.('email-verification');
      continueButton.focus?.();

      const setBusy = (busy) => {
        continueButton.disabled = busy;
        resendButton.disabled = busy;
      };

      continueButton.addEventListener('click', async () => {
        setBusy(true);
        status.textContent = '';
        try {
          const { verified } = await Firebase.refreshEmailVerification();
          if (verified) {
            remove();
            resolve('verified');
            return;
          }
          status.textContent = uiText(
            'emailVerify.notYet',
            "We can't see the verification yet. Open the link in the email, then try again.",
          );
        } catch (error) {
          console.warn('[EmailVerificationGate] Refresh failed:', error?.code || error?.message || error);
          status.textContent = uiText('emailVerify.refreshFailed', 'We could not check right now. Try again in a moment.');
        } finally {
          if (overlay) setBusy(false);
        }
      });

      resendButton.addEventListener('click', async () => {
        setBusy(true);
        const result = await Firebase.sendVerificationEmail();
        status.textContent = result?.ok
          ? uiText('emailVerify.resent', 'Sent. Check your inbox and your spam folder.')
          : uiText('emailVerify.resendFailed', "We couldn't send it right now. Try again in a minute.");
        if (overlay) setBusy(false);
      });

      switchButton.addEventListener('click', () => {
        remove();
        resolve('switch_account');
      });
    });
  }

  return { waitForVerification, hide: remove };
})();

export { EmailVerificationGate };
